export const getNextGalleryIndex = (current, delta, total) => {
    const count = Math.max(0, Number.parseInt(total, 10) || 0);

    if (count <= 1) {
        return 0;
    }

    const index = Number.parseInt(current, 10) || 0;

    return ((index + delta) % count + count) % count;
};

export const formatGalleryCounter = (current, total) => {
    const count = Math.max(1, Number.parseInt(total, 10) || 1);
    const index = Math.min(count, Math.max(1, (Number.parseInt(current, 10) || 0) + 1));
    const digits = Math.max(2, String(count).length);

    return `${String(index).padStart(digits, '0')} / ${String(count).padStart(digits, '0')}`;
};

export const isColorAttributeKey = (key) => {
    const normalized = String(key || '').toLowerCase().replaceAll('-', '_');

    return /(^|_)(kolor|color)($|_)/.test(normalized);
};

/**
 * Carries a selected size (and other non-colour variation choices) to another
 * independently managed colour product without leaking the current colour.
 */
export const buildLinkedColorProductUrl = (baseUrl, selections = {}) => {
    const source = String(baseUrl || '');

    if (source === '') {
        return '';
    }

    const isAbsolute = /^[a-z][a-z\d+.-]*:/i.test(source);
    let url;

    try {
        url = new URL(source, 'https://seluvia.invalid');
    } catch {
        return source;
    }

    [...url.searchParams.keys()].forEach((key) => {
        if (/^attribute_[a-z0-9_-]+$/i.test(key)) {
            url.searchParams.delete(key);
        }
    });

    Object.entries(selections && typeof selections === 'object' ? selections : {}).forEach(([key, value]) => {
        const safeKey = String(key || '').toLowerCase();
        const safeValue = String(value || '').trim();

        if (!/^attribute_[a-z0-9_-]+$/.test(safeKey)
            || isColorAttributeKey(safeKey)
            || safeValue === ''
            || safeValue.length > 100
        ) {
            return;
        }

        url.searchParams.set(safeKey, safeValue);
    });

    return isAbsolute
        ? url.toString()
        : `${url.pathname}${url.search}${url.hash}`;
};

const isPurchasableVariation = (variation) => Boolean(
    variation
    && variation.variation_is_active !== false
    && variation.variation_is_visible !== false
    && variation.is_purchasable !== false
    && variation.is_in_stock !== false,
);

const variationMatchesSelection = (variation, selections, attributeNames, partial) => {
    if (!isPurchasableVariation(variation)) {
        return false;
    }

    return attributeNames.every((attributeName) => {
        const selected = String(selections?.[attributeName] || '');
        const expected = String(variation?.attributes?.[attributeName] || '');

        if (selected === '') {
            return partial;
        }

        return expected === '' || expected === selected;
    });
};

export const findBundleVariation = (variations, selections, attributeNames) => {
    const candidates = Array.isArray(variations) ? variations : [];
    const names = Array.isArray(attributeNames) ? attributeNames : [];

    if (!names.length || names.some((name) => String(selections?.[name] || '') === '')) {
        return null;
    }

    return candidates.find((variation) => (
        variationMatchesSelection(variation, selections, names, false)
    )) || null;
};

export const isBundleOptionAvailable = (
    variations,
    selections,
    attributeNames,
    attributeName,
    value,
) => {
    const candidates = Array.isArray(variations) ? variations : [];
    const names = Array.isArray(attributeNames) ? attributeNames : [];
    const optionValue = String(value || '');

    if (!names.includes(attributeName) || optionValue === '') {
        return false;
    }

    const candidateSelections = {
        ...(selections && typeof selections === 'object' ? selections : {}),
        [attributeName]: optionValue,
    };

    return candidates.some((variation) => (
        variationMatchesSelection(variation, candidateSelections, names, true)
    ));
};
